import React, { useEffect, useCallback, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { toast } from "@/utils/appToast";
import { api } from "@/utils/api";
import { loginUser } from "@/features/user/userSlice";
import { SetStorage } from "@/shared/services/credentialsService";

interface GoogleSignInSuccessPayload {
  token: string;
  user: any;
  workerProfile?: any;
  workerProfileIncluded: boolean;
}

interface GoogleSignInProps {
  onSuccess?: (payload: GoogleSignInSuccessPayload) => void | Promise<void>;
}

const GoogleSignIn: React.FC<GoogleSignInProps> = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const onSuccessRef = useRef(onSuccess);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
  }, [onSuccess]);

  const handleCredentialResponse = useCallback(
    async (response: { credential: string }) => {
      try {
        const token = response.credential;
        if (!token) throw new Error("Google token not received");

        setIsLoading(true);

        const { status, data } = await api.googleSignIn({ token });

        if (status === "success") {
          const { token: userToken, user } = data;
          const workerProfileIncluded = Object.prototype.hasOwnProperty.call(
            data,
            "workerProfile"
          );

          dispatch(loginUser({ token: userToken, user }));

          SetStorage(
            {
              data: {
                token: userToken,
                user: user,
              },
            },
            true
          );

          if (onSuccessRef.current) {
            await onSuccessRef.current({
              token: userToken,
              user,
              workerProfile: data.workerProfile,
              workerProfileIncluded,
            });
          }
        } else {
          toast.error(data.message || "Sign-in failed");
        }
      } catch (err: any) {
        console.error("Google sign-in error:", err);
        toast.error(err.message || "Google sign-in failed");
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch]
  );

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;
    let attempts = 0;
    const maxAttempts = 50;

    const tryInitialize = () => {
      if (cancelled) return;

      const google = (window as any).google;
      const buttonEl = document.getElementById("google-signin-button");

      if (google?.accounts?.id && buttonEl) {
        google.accounts.id.initialize({
          client_id: (import.meta as any).env.VITE_APP_GOOGLE_CLIENT_ID,
          callback: handleCredentialResponse,
        });

        google.accounts.id.renderButton(buttonEl, {
          theme: "outline",
          size: "large",
          width: "100%",
        });
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        pollTimer = setTimeout(tryInitialize, 100);
      }
    };

    tryInitialize();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [handleCredentialResponse]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <div
        id="google-signin-button"
        style={{
          width: "100%",
          opacity: isLoading ? 0.6 : 1,
          pointerEvents: isLoading ? "none" : "auto",
        }}
      />
      {isLoading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            className="spinner-border"
            style={{ width: "20px", height: "20px" }}
            role="status"
          />
        </div>
      )}
    </div>
  );
};

export default GoogleSignIn;
